/**
 * src/components/agent/AgentModeBadge.jsx — F5 (2026-04-21)
 *
 * Visible pill that tells the user which audio pipeline currently owns
 * the mic and the output. Reads agentMode from the context. Auto-hides
 * in idle mode so it doesn't clutter the chrome when nothing is active.
 *
 * Chris's acceptance (2026-04-21 bug report §5):
 *   "Always show: user transcript / agent response text / audio tied
 *    to that message. Visible state badge: 🎤 Listening / 🧠 Processing
 *    / 🗣️ Responding."
 *
 * This is the minimum useful badge for shell-level mode display. The
 * per-phase detail (listening vs processing vs speaking during a
 * conversation turn) lives inside VoiceModeOverlay where the state
 * machine is visible. This badge signals the WHICH PIPELINE level so
 * the user knows whether dictation or conversation is active even
 * when the overlay is closed.
 */

import { useAgentMode } from '../../lib/agent/agentModeContext.jsx';

const VARIANTS = {
  capture: {
    label: 'Capturing',
    dot: '●',
    bg: 'var(--warn-tint)',
    fg: 'var(--warn)',
  },
  conversation: {
    label: 'Voice',
    dot: '●',
    bg: 'var(--ok-tint)',
    fg: 'var(--ok)',
  },
};

const pillStyle = (v) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  background: v.bg,
  color: v.fg,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.2,
  letterSpacing: 0.2,
  userSelect: 'none',
  pointerEvents: 'none',
});

const dotStyle = (v) => ({
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: v.fg,
  animation: 'pulse 1.6s ease-in-out infinite',
});

export default function AgentModeBadge() {
  const { mode } = useAgentMode();
  const variant = VARIANTS[mode];
  if (!variant) return null; // idle → hide

  return (
    <div
      data-testid="agent-mode-badge"
      data-mode={mode}
      style={pillStyle(variant)}
      role="status"
      aria-live="polite"
      aria-label={`Agent mode: ${variant.label}`}
    >
      <span style={dotStyle(variant)} />
      {variant.label}
    </div>
  );
}
